/**
 * WorkflowEditorPage — full page for editing a form's approval workflow.
 * Replaces the cramped modal. Layout:
 *   ┌──────────────┬───────────────────────────────────────┐
 *   │ Step rail    │ Active step editor                    │
 *   │  (jump nav)  │  - name / assignees / field controls  │
 *   └──────────────┴───────────────────────────────────────┘
 */
import { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { PageContainer } from "@/components/app/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    ArrowLeft, FloppyDisk, Plus, Trash, GitBranch, EyeSlash, LockSimple, Asterisk,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const CONTROL_LABELS = {
    hide: { label: "Hide", icon: EyeSlash, tone: "text-muted-foreground" },
    readonly: { label: "Read-only", icon: LockSimple, tone: "text-warning" },
    mandatory: { label: "Mandatory", icon: Asterisk, tone: "text-destructive" },
};

export default function WorkflowEditorPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form, setForm] = useState(null);
    const [fields, setFields] = useState([]);
    const [sections, setSections] = useState([]);
    const [users, setUsers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [roles, setRoles] = useState([]);
    const [wf, setWf] = useState({ workflow_enabled: false, workflow_steps: [] });
    const [activeIdx, setActiveIdx] = useState(0);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    const load = useCallback(async () => {
        try {
            const [meta, u, g, r] = await Promise.all([
                api.get(`/forms/${id}`),
                api.get("/users"),
                api.get("/groups"),
                api.get("/roles"),
            ]);
            setForm(meta.data.form); setFields(meta.data.fields); setSections(meta.data.sections || []);
            setUsers(u.data); setGroups(g.data); setRoles(r.data);
            const steps = (meta.data.form.workflow_steps || []).map((s) => ({
                ...s,
                field_controls: s.field_controls || {},
                section_controls: s.section_controls || {},
            }));
            setWf({ workflow_enabled: !!meta.data.form.workflow_enabled, workflow_steps: steps });
            setActiveIdx(Math.min(0, steps.length - 1));
            setDirty(false);
        } catch (e) { toast.error(extractError(e)); }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    const markDirty = (next) => { setWf(next); setDirty(true); };

    const addStep = () => {
        const newId = `step-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        const next = {
            ...wf,
            workflow_steps: [
                ...wf.workflow_steps,
                {
                    id: newId,
                    name: `Step ${wf.workflow_steps.length + 1}`,
                    assignee_type: "user",
                    assignee_ids: [],
                    order: wf.workflow_steps.length,
                    field_controls: {},
                    section_controls: {},
                },
            ],
        };
        markDirty(next);
        setActiveIdx(next.workflow_steps.length - 1);
    };

    const updateStep = (i, patch) => {
        const next = [...wf.workflow_steps];
        next[i] = { ...next[i], ...patch };
        markDirty({ ...wf, workflow_steps: next });
    };

    const removeStep = (i) => {
        if (!confirm(`Remove step "${wf.workflow_steps[i].name}"?`)) return;
        const next = wf.workflow_steps.filter((_, j) => j !== i).map((s, idx) => ({ ...s, order: idx }));
        markDirty({ ...wf, workflow_steps: next });
        if (activeIdx >= next.length) setActiveIdx(Math.max(0, next.length - 1));
    };

    const moveStep = (i, dir) => {
        const t = i + dir;
        if (t < 0 || t >= wf.workflow_steps.length) return;
        const next = [...wf.workflow_steps];
        [next[i], next[t]] = [next[t], next[i]];
        next.forEach((s, idx) => { s.order = idx; });
        markDirty({ ...wf, workflow_steps: next });
        setActiveIdx(t);
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/forms/${id}/workflow`, wf);
            toast.success("Workflow saved");
            setDirty(false);
            await load();
        } catch (e) { toast.error(extractError(e)); }
        finally { setSaving(false); }
    };

    const setStepFc = (i, fname, val) => {
        const step = wf.workflow_steps[i];
        const next = { ...(step.field_controls || {}) };
        if (!val || val === "__inherit__") delete next[fname];
        else next[fname] = val;
        updateStep(i, { field_controls: next });
    };

    const setStepSc = (i, sectionId, hide) => {
        const step = wf.workflow_steps[i];
        const next = { ...(step.section_controls || {}) };
        if (hide) next[sectionId] = "hide";
        else delete next[sectionId];
        updateStep(i, { section_controls: next });
    };

    // Build section -> [fields] map (synthetic "__all__" bucket for sectionless fields)
    const fieldGroups = useMemo(() => {
        const grouped = {};
        fields.forEach((f) => {
            const sid = f.section_id || "__all__";
            (grouped[sid] = grouped[sid] || []).push(f);
        });
        const sorted = [...(sections || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
        const out = [];
        sorted.forEach((s) => {
            if (grouped[s.id]?.length) out.push({ id: s.id, title: s.title, fields: grouped[s.id], synthetic: false });
        });
        if (grouped["__all__"]?.length) {
            out.push({ id: "__all__", title: sections?.length ? "Unsectioned" : "All Fields", fields: grouped["__all__"], synthetic: true });
        }
        return out;
    }, [fields, sections]);

    const activeStep = wf.workflow_steps[activeIdx] || null;
    const activeOpts = useMemo(() => {
        if (!activeStep) return [];
        return activeStep.assignee_type === "user" ? users : activeStep.assignee_type === "group" ? groups : roles;
    }, [activeStep, users, groups, roles]);

    const fcCountFor = (step) => Object.keys(step.field_controls || {}).length + Object.keys(step.section_controls || {}).length;

    if (!form) return <PageContainer><div className="text-sm text-muted-foreground">Loading…</div></PageContainer>;

    return (
        <PageContainer>
            <div className="flex items-start justify-between gap-4 mb-6" data-testid="wf-header">
                <div className="min-w-0">
                    <Link to={`/forms/${id}/builder`} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2" data-testid="wf-back-link">
                        <ArrowLeft size={12} /> Back to builder
                    </Link>
                    <div className="label-overline flex items-center gap-2"><GitBranch size={12} /> Workflow editor</div>
                    <h1 className="text-3xl font-bold tracking-tight">{form.form_title || form.form_name}</h1>
                    <p className="text-sm text-muted-foreground mt-1">Define the approval chain and per-step field overrides for this app.</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 border border-border rounded-sm px-3 py-2" data-testid="wf-enabled-wrap">
                        <span className="text-xs font-medium">Workflow enabled</span>
                        <Switch checked={wf.workflow_enabled} onCheckedChange={(v) => markDirty({ ...wf, workflow_enabled: v })} data-testid="workflow-enabled-switch" />
                    </div>
                    <Button onClick={save} disabled={!dirty || saving} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="wf-save-btn">
                        <FloppyDisk size={14} className="mr-1" /> {saving ? "Saving…" : (dirty ? "Save changes" : "Saved")}
                    </Button>
                </div>
            </div>

            <div className="grid lg:grid-cols-[280px_1fr] gap-6 min-h-[60vh]">
                {/* LEFT: step navigator */}
                <aside className="brut-section p-4 self-start sticky top-6" data-testid="wf-step-rail">
                    <div className="flex items-center justify-between mb-3">
                        <div className="label-overline">Steps ({wf.workflow_steps.length})</div>
                        <Button size="icon" variant="ghost" className="h-7 w-7 rounded-sm" onClick={addStep} title="Add step" data-testid="wf-add-step-btn">
                            <Plus size={14} />
                        </Button>
                    </div>
                    {wf.workflow_steps.length === 0 ? (
                        <div className="border border-dashed border-border rounded-sm p-4 text-xs text-muted-foreground text-center" data-testid="wf-rail-empty">
                            No steps yet. Click + to add your first approval step.
                        </div>
                    ) : (
                        <ul className="space-y-1.5">
                            {wf.workflow_steps.map((s, i) => {
                                const active = i === activeIdx;
                                return (
                                    <li key={s.id}>
                                        <button
                                            type="button"
                                            onClick={() => setActiveIdx(i)}
                                            data-testid={`wf-rail-step-${i}`}
                                            className={`w-full text-left rounded-sm px-3 py-2 border transition-colors group ${
                                                active ? "bg-primary/10 border-primary/40" : "bg-secondary/30 border-border hover:bg-secondary/60"
                                            }`}
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-1.5">
                                                        <span className={`font-mono text-[10px] tabular-nums ${active ? "text-primary" : "text-muted-foreground"}`}>#{i + 1}</span>
                                                        <span className="text-sm font-medium truncate">{s.name || "Untitled"}</span>
                                                    </div>
                                                    <div className="mt-1 flex items-center gap-1 flex-wrap">
                                                        <Badge variant="outline" className="rounded-sm font-mono text-[9px] py-0">{s.assignee_type}·{(s.assignee_ids || []).length}</Badge>
                                                        {fcCountFor(s) > 0 && (
                                                            <Badge variant="outline" className="rounded-sm font-mono text-[9px] py-0 border-primary/40 text-primary" data-testid={`wf-rail-fc-${i}`}>
                                                                {fcCountFor(s)}fc
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="opacity-0 group-hover:opacity-100 flex flex-col">
                                                    <span role="button" tabIndex={-1} onClick={(e) => { e.stopPropagation(); moveStep(i, -1); }} className={`text-[10px] leading-none p-0.5 cursor-pointer ${i === 0 ? "opacity-30 pointer-events-none" : ""}`} data-testid={`wf-step-up-${i}`}>▲</span>
                                                    <span role="button" tabIndex={-1} onClick={(e) => { e.stopPropagation(); moveStep(i, 1); }} className={`text-[10px] leading-none p-0.5 cursor-pointer ${i === wf.workflow_steps.length - 1 ? "opacity-30 pointer-events-none" : ""}`} data-testid={`wf-step-down-${i}`}>▼</span>
                                                </div>
                                            </div>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </aside>

                {/* RIGHT: active step editor */}
                <section className="brut-section p-8" data-testid="wf-step-editor">
                    {!activeStep ? (
                        <div className="h-full flex flex-col items-center justify-center text-center text-sm text-muted-foreground py-20">
                            <GitBranch size={36} weight="duotone" className="text-primary mb-4 opacity-60" />
                            <p className="max-w-xs">Add a step from the rail to start defining the approval chain for <span className="font-semibold text-foreground">{form.form_title || form.form_name}</span>.</p>
                        </div>
                    ) : (
                        <div className="space-y-8">
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                    <div className="label-overline mb-1">Step #{activeIdx + 1}</div>
                                    <Input
                                        value={activeStep.name}
                                        onChange={(e) => updateStep(activeIdx, { name: e.target.value })}
                                        className="text-xl font-bold tracking-tight border-0 px-0 h-auto py-0 focus-visible:ring-0 bg-transparent shadow-none rounded-none"
                                        placeholder="e.g. Manager review"
                                        data-testid="wf-step-name-input"
                                    />
                                </div>
                                <Button variant="outline" size="sm" onClick={() => removeStep(activeIdx)} className="rounded-sm text-destructive hover:text-destructive" data-testid="wf-remove-step-btn">
                                    <Trash size={14} className="mr-1" /> Remove step
                                </Button>
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <Label className="label-overline">Assignee type</Label>
                                    <Select value={activeStep.assignee_type} onValueChange={(v) => updateStep(activeIdx, { assignee_type: v, assignee_ids: [] })}>
                                        <SelectTrigger className="mt-2 rounded-sm" data-testid="wf-assignee-type-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="user">Specific user(s)</SelectItem>
                                            <SelectItem value="group">Any of group</SelectItem>
                                            <SelectItem value="role">Any with role</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <p className="text-[11px] text-muted-foreground mt-2">
                                        {activeStep.assignee_type === "user" && "Any of the selected users may approve / reject."}
                                        {activeStep.assignee_type === "group" && "Any member of any selected group may approve / reject."}
                                        {activeStep.assignee_type === "role" && "Any user holding any of these roles may approve / reject."}
                                    </p>
                                </div>
                                <div>
                                    <Label className="label-overline">Approvers ({(activeStep.assignee_ids || []).length})</Label>
                                    <div className="mt-2 border border-border rounded-sm max-h-60 overflow-y-auto" data-testid="wf-approvers-list">
                                        {activeOpts.length === 0 ? (
                                            <div className="text-xs text-muted-foreground p-3">No {activeStep.assignee_type}s available.</div>
                                        ) : activeOpts.map((o) => (
                                            <label key={o.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-secondary/50 border-b border-border last:border-b-0">
                                                <input
                                                    type="checkbox"
                                                    checked={(activeStep.assignee_ids || []).includes(o.id)}
                                                    onChange={(e) => updateStep(activeIdx, {
                                                        assignee_ids: e.target.checked
                                                            ? [...(activeStep.assignee_ids || []), o.id]
                                                            : (activeStep.assignee_ids || []).filter((x) => x !== o.id),
                                                    })}
                                                    data-testid={`wf-approver-${o.id}`}
                                                />
                                                <span className="truncate flex-1">{o.name || o.email}</span>
                                                {o.code && <code className="text-[10px] font-mono text-muted-foreground">{o.code}</code>}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Field controls */}
                            <div className="border-t border-border pt-6">
                                <div className="flex items-center justify-between mb-3">
                                    <div>
                                        <Label className="label-overline">Field controls at this step</Label>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Override field behaviour while a record is sitting at this step.
                                            <span className="ml-1">Empty = inherit the field's own setting.</span>
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2 text-[10px] font-mono text-muted-foreground">
                                        {Object.entries(CONTROL_LABELS).map(([k, v]) => {
                                            const Ico = v.icon;
                                            return (
                                                <span key={k} className="inline-flex items-center gap-1">
                                                    <Ico size={11} className={v.tone} /> {v.label}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                                {fields.length === 0 ? (
                                    <div className="border border-dashed border-border rounded-sm p-6 text-sm text-muted-foreground text-center" data-testid="wf-fc-empty">
                                        Add some fields to this form first before configuring per-step controls.
                                    </div>
                                ) : (
                                    <div className="space-y-4" data-testid="wf-fc-grid">
                                        {fieldGroups.map((g) => {
                                            const sectionHidden = activeStep.section_controls?.[g.id] === "hide";
                                            return (
                                                <div key={g.id} className={`border rounded-sm transition-colors ${sectionHidden ? "border-primary/40 bg-primary/5" : "border-border"}`} data-testid={`wf-fc-section-${g.id}`}>
                                                    <div className="flex items-center justify-between gap-3 px-3 py-2 bg-secondary/30 border-b border-border">
                                                        <div className="min-w-0">
                                                            <div className="text-xs font-semibold uppercase tracking-wider truncate flex items-center gap-2">
                                                                <span>{g.title}</span>
                                                                <span className="text-[10px] font-mono text-muted-foreground normal-case tracking-normal">{g.fields.length} field{g.fields.length === 1 ? "" : "s"}</span>
                                                            </div>
                                                        </div>
                                                        <label className="flex items-center gap-2 text-xs whitespace-nowrap" data-testid={`wf-sec-hide-${g.id}`}>
                                                            <EyeSlash size={12} className="text-muted-foreground" />
                                                            <span>Hide whole section</span>
                                                            <Switch checked={sectionHidden} onCheckedChange={(c) => setStepSc(activeIdx, g.id, !!c)} />
                                                        </label>
                                                    </div>
                                                    {!sectionHidden && (
                                                        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-2 p-3">
                                                            {g.fields.map((fl) => {
                                                                const cur = activeStep.field_controls?.[fl.field_name] || "";
                                                                return (
                                                                    <div key={fl.id} className={`flex items-center gap-2 border rounded-sm px-3 py-2 transition-colors ${cur ? "border-primary/40 bg-primary/5" : "border-border"}`}>
                                                                        <div className="flex-1 min-w-0">
                                                                            <div className="text-sm font-medium truncate">{fl.field_label}</div>
                                                                            <div className="text-[10px] font-mono text-muted-foreground truncate">{fl.field_name} · {fl.field_type}</div>
                                                                        </div>
                                                                        <Select value={cur || "__inherit__"} onValueChange={(v) => setStepFc(activeIdx, fl.field_name, v)}>
                                                                            <SelectTrigger className="rounded-sm h-8 w-32 text-xs" data-testid={`wf-fc-${fl.field_name}`}><SelectValue /></SelectTrigger>
                                                                            <SelectContent>
                                                                                <SelectItem value="__inherit__">Inherit</SelectItem>
                                                                                <SelectItem value="hide">Hide</SelectItem>
                                                                                <SelectItem value="readonly">Read-only</SelectItem>
                                                                                <SelectItem value="mandatory">Mandatory</SelectItem>
                                                                            </SelectContent>
                                                                        </Select>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                    {sectionHidden && (
                                                        <div className="px-3 py-2 text-xs text-muted-foreground italic">All {g.fields.length} field{g.fields.length === 1 ? "" : "s"} in this section will be hidden at this step. Toggle off to configure individually.</div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </section>
            </div>

            {dirty && (
                <div className="fixed bottom-6 right-6 brut-section p-4 bg-card shadow-lg flex items-center gap-3" data-testid="wf-dirty-banner">
                    <span className="text-sm">You have unsaved changes</span>
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/forms/${id}/builder`)} className="rounded-sm">Discard</Button>
                    <Button size="sm" onClick={save} disabled={saving} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground">
                        <FloppyDisk size={12} className="mr-1" /> {saving ? "Saving…" : "Save"}
                    </Button>
                </div>
            )}
        </PageContainer>
    );
}
